import type { Clip, Pose } from './pose';

const TAU = Math.PI * 2;

export function wolfIdle(t: number): Pose {
  const b = Math.sin(t * 2.2);
  return {
    joints: {
      body: [b * 0.01, 0, 0],
      chest: [0, 0, 0],
      neck: [0.1, Math.sin(t * 0.5) * 0.25, 0],
      head: [0.15 + b * 0.02, Math.sin(t * 0.5) * 0.2, 0],
      jaw: [0.05 + Math.max(0, Math.sin(t * 3)) * 0.12, 0, 0],
      tail1: [0.3, Math.sin(t * 1.5) * 0.25, 0],
      tail2: [0.2, Math.sin(t * 1.5 - 0.6) * 0.3, 0],
      legFL: [0, 0, 0],
      kneeFL: [0, 0, 0],
      legFR: [0, 0, 0],
      kneeFR: [0, 0, 0],
      legBL: [0.05, 0, 0],
      kneeBL: [-0.05, 0, 0],
      legBR: [0.05, 0, 0],
      kneeBR: [-0.05, 0, 0],
    },
    rootY: b * 0.006,
  };
}

/** Trot/gallop cycle, `run` 0..1. Phase advances with distance. */
export function wolfGait(phase: number, run: number): Pose {
  const f = phase * TAU;
  const amp = 0.45 + 0.35 * run;
  const s = Math.sin(f);
  const s2 = Math.sin(f + Math.PI * (0.5 + 0.4 * run));
  const k = (x: number): number => Math.max(0, Math.sin(x)) * (0.6 + 0.5 * run);
  return {
    joints: {
      body: [Math.sin(f * 2) * 0.04 * run, 0, 0],
      chest: [Math.sin(f) * 0.06 * run, 0, 0],
      neck: [0.25 + 0.15 * run, 0, 0],
      head: [-0.05, Math.sin(f) * 0.05, 0],
      jaw: [0.15 * run, 0, 0],
      tail1: [0.5 - 0.4 * run, Math.sin(f) * 0.2, 0],
      tail2: [0.1, Math.sin(f - 0.8) * 0.3, 0],
      legFL: [-s * amp, 0, 0],
      kneeFL: [-k(f + 1.4), 0, 0],
      legFR: [-s2 * amp, 0, 0],
      kneeFR: [-k(f + Math.PI * (0.5 + 0.4 * run) + 1.4), 0, 0],
      legBL: [s2 * amp, 0, 0],
      kneeBL: [k(f + Math.PI * (0.5 + 0.4 * run) - 0.4), 0, 0],
      legBR: [s * amp, 0, 0],
      kneeBR: [k(f - 0.4), 0, 0],
    },
    rootY: Math.abs(Math.sin(f)) * 0.04 * (0.5 + run),
  };
}

export const WOLF_CLIPS: Record<string, Clip> = {
  ATTACK_1: {
    duration: 0.7,
    keys: [
      {
        t: 0,
        pose: {
          joints: {
            neck: [0.35, 0, 0],
            head: [0.2, 0, 0],
            legBL: [0.3, 0, 0],
            legBR: [0.3, 0, 0],
            kneeBL: [0.5, 0, 0],
            kneeBR: [0.5, 0, 0],
            jaw: [0.1, 0, 0],
          },
          rootY: -0.12,
          rootZ: -0.1,
        },
      },
      {
        t: 0.45,
        pose: {
          joints: {
            neck: [0.45, 0, 0],
            head: [0.15, 0, 0],
            legBL: [0.5, 0, 0],
            legBR: [0.5, 0, 0],
            kneeBL: [0.8, 0, 0],
            kneeBR: [0.8, 0, 0],
            jaw: [0.6, 0, 0],
          },
          rootY: -0.18,
          rootZ: -0.18,
        },
      },
      {
        t: 0.58,
        pose: {
          joints: {
            neck: [-0.1, 0, 0],
            head: [-0.2, 0, 0],
            legFL: [-0.8, 0, 0],
            legFR: [-0.7, 0, 0],
            legBL: [-0.2, 0, 0],
            legBR: [-0.2, 0, 0],
            jaw: [0.05, 0, 0],
          },
          rootY: 0.08,
          rootZ: 0.45,
        },
      },
      { t: 1, pose: { joints: { neck: [0.2, 0, 0] }, rootY: 0, rootZ: 0 } },
    ],
  },
  DEATH: {
    duration: 1.0,
    hold: true,
    keys: [
      { t: 0, pose: { joints: { neck: [0.3, 0, 0] } } },
      {
        t: 0.4,
        pose: {
          joints: {
            neck: [0.5, 0, 0],
            legFL: [-0.4, 0, 0],
            legFR: [-0.4, 0, 0],
            kneeFL: [-1.2, 0, 0],
            kneeFR: [-1.2, 0, 0],
          },
          rootY: -0.25,
        },
      },
      {
        t: 1,
        pose: {
          joints: {
            neck: [-0.3, 0.3, 0],
            head: [-0.2, 0, 0],
            legFL: [-0.6, 0, 0.3],
            legFR: [-0.5, 0, 0.2],
            legBL: [0.4, 0, 0.3],
            legBR: [0.5, 0, 0.2],
            tail1: [0.9, 0, 0],
          },
          rootY: -0.42,
          rootRoll: 1.45,
        },
      },
    ],
  },
};

export const WOLF_FLINCH: Pose = {
  joints: { neck: [-0.3, 0, 0.15], head: [-0.3, 0, 0], body: [0, 0, 0.08] },
  rootZ: -0.06,
};
